'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useRef, useState } from 'react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { cx } from 'class-variance-authority';
import { LuCalculator, LuChevronDown, LuMapPin, LuTriangleAlert } from 'react-icons/lu';
import { useI18n } from '~/features/i18n';
import { Units } from '~/modules/units';
import { LAST_LOCATION_MODE, LAST_LOCATION_STORAGE } from '~/features/activities/lastLocation';
import type { PartnerOption } from './data/getGroupPartners';
import { rankPartners, type RankedPartner } from './utils/partnerRanking';
import { haversineKm } from './utils/haversine';
import { calcTransportCost, type TransportEstimate } from './utils/calcTransportCost';
import { findClosestPartnerLocation } from './utils/findClosestPartnerLocation';
import { partnerComesToYou } from './utils/partnerComesToYou';
import { partnerHasFixedVenue } from './utils/partnerHasFixedVenue';
import { shortLocationLabel } from './utils/shortLocationLabel';
import { unpinnedLocationStatement } from './utils/locationStatement';
import { selectCheaperAlternative } from './utils/cheaperAlternative';
import { splitFeaturedAlternative } from './utils/featuredAlternative';
import { fetchDrivingRoute } from './mapbox/directions';
import { setDrivingRoute, useDrivingRouteStore } from './drivingRouteState';
import { ModeTabs } from './components/ModeTabs';
import { LocationInputRow } from './components/LocationInputRow';
import { PartnerResultCard } from './components/PartnerResultCard';
import { TransportCostCard } from './components/TransportCostCard';
import type { PresetSeed } from './presetSeed';
import { ActivityPDP_useCtx } from '../context';
import type { WidgetMode } from './types';
import { seedWidgetStore, setWidgetFixedAddress, setWidgetLocation, setWidgetMode, setWidgetVenueCities, useLocationWidgetStore } from './store';
import { venueCity } from './utils/venueCity';

// The map pulls in mapbox-gl (a sizeable client-only bundle) — load it lazily,
// client-side only, so it never runs during SSR.
const PartnerMap = dynamic(() => import('./components/PartnerMap').then(m => ({
  default: m.PartnerMap
})), {
  ssr: false
});

/**
 * Nearest first. These lists answer "this provider cannot host you — who can?", so distance is
 * the only ordering that helps; ranking order left the closest option buried. Partners with no
 * known distance sort last rather than jumping to the front.
 */
function byDistance(list: ReadonlyArray<RankedPartner>): RankedPartner[] {
  return [...list].sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity));
}

/** Only surface a cheaper alternative when it beats the selected partner's transport by more than this fraction. */
const CHEAPER_ALTERNATIVE_THRESHOLD = 0.2;
type CustomEntry = {
  partner: PartnerOption;
  distanceKm: number | null;
  transport: TransportEstimate;
};
type Props = {
  partners: PartnerOption[];
  availableRegionNames: string[];
  /** Whether ANY partner in the group travels ("Aanbieder komt naar mij" tab available). */
  comesToYou: boolean;
  /** Whether ANY partner has a fixed venue ("Ik ga naar de aanbieder" tab available). */
  hasFixedLocation: boolean;
  /** Whether the currently-viewed activity's own partner travels to the customer. */
  currentComesToYou: boolean;
  /** Whether the currently-viewed activity's own partner has a venue you can visit. */
  currentHasFixedVenue: boolean;
  /**
   * The activity is set at a fixed location whose address the partner never entered. There
   * is nothing to pin, but there is somewhere to go — so the widget says so rather than
   * leaving the tab looking unavailable.
   */
  currentVenueIsUnpinned: boolean;
  /** The activity id of the PDP being viewed — its partner is always highlighted. */
  currentActivityId: number;
  /**
   * The `/teambuildings` filter the visitor arrived with, resolved on the server. It drives
   * the participant count and therefore every price on screen, so it cannot be read from the
   * URL here: the server would render one count and the browser another.
   */
  preset: PresetSeed;
};
export const LocationWidget: React.FC<Props> = t0 => {
  const $ = _c(133);
  const {
    partners,
    availableRegionNames,
    comesToYou,
    hasFixedLocation,
    currentComesToYou,
    currentHasFixedVenue,
    currentVenueIsUnpinned,
    currentActivityId,
    preset
  } = t0;
  const i18n = useI18n();
  const {
    mode: storedMode,
    location,
    fixedAddressId
  } = useLocationWidgetStore();
  const {
    getRoute: getDrivingRoute
  } = useDrivingRouteStore();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = parseAsStringLiteral(["fixed", "custom"] as const);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [urlMode, setUrlMode] = useQueryState("locationMode", t1);
  let t2;
  if ($[1] !== currentActivityId || $[2] !== partners) {
    t2 = () => partners.some(p => p.activityId === currentActivityId) ? currentActivityId : null;
    $[1] = currentActivityId;
    $[2] = partners;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [pickedActivityId, setPickedActivityId] = useState(t2);
  const [hoveredActivityId, setHoveredActivityId] = useState(null);
  const [showAllAlternatives, setShowAllAlternatives] = useState(false);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ActivityPDP_useCtx();
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const participants = t3.participants.state;
  const locationInputWrap = useRef(null);
  let t4;
  if ($[5] !== currentActivityId || $[6] !== partners) {
    t4 = partners.find(p_0 => p_0.activityId === currentActivityId)?.transportCostTiers.length;
    $[5] = currentActivityId;
    $[6] = partners;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const canQuoteTransport = !!t4;
  const [didAskForLocation, setDidAskForLocation] = useState(false);
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setDidAskForLocation(true);
      locationInputWrap.current?.querySelector("[aria-expanded]")?.click();
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const focusLocationInput = t5;
  let t6;
  if ($[9] !== currentActivityId || $[10] !== partners || $[11] !== preset.location || $[12] !== preset.mode || $[13] !== urlMode) {
    t6 = () => {
      const current = partners.find(partner => partner.activityId === currentActivityId);
      setWidgetVenueCities(Object.fromEntries((current?.addresses ?? []).map(address => [String(address.id), venueCity(address, current?.partnerCity)])));
      const stored = LAST_LOCATION_STORAGE.getValue();
      seedWidgetStore({
        activityId: currentActivityId,
        mode: resolveSeedMode({
          urlMode,
          presetMode: preset.mode,
          storedMode: LAST_LOCATION_MODE.getValue()
        }),
        location: preset.location ?? (stored != null ? {
          query: stored.query,
          lat: stored.lat,
          lng: stored.lng
        } : null)
      });
    };
    $[9] = currentActivityId;
    $[10] = partners;
    $[11] = preset.location;
    $[12] = preset.mode;
    $[13] = urlMode;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  useMountEffect(t6);
  const mode = storedMode === "custom" && !comesToYou && hasFixedLocation ? "fixed" : storedMode === "fixed" && !hasFixedLocation && comesToYou ? "custom" : storedMode;
  const userLabel = shortLocationLabel(location?.query);
  let t7;
  if ($[15] !== mode || $[16] !== partners) {
    let t8;
    if ($[18] !== mode) {
      t8 = partner_0 => mode === "custom" ? partnerComesToYou(partner_0) : partnerHasFixedVenue(partner_0);
      $[18] = mode;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    t7 = partners.filter(t8);
    $[15] = mode;
    $[16] = partners;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const eligiblePartners = t7;
  let t8;
  if ($[20] !== eligiblePartners || $[21] !== location || $[22] !== mode) {
    t8 = rankPartners({
      partners: eligiblePartners,
      participants,
      userLocation: location ? {
        lat: location.lat,
        lng: location.lng
      } : null,
      venuesOnly: mode !== "custom"
    });
    $[20] = eligiblePartners;
    $[21] = location;
    $[22] = mode;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const ranked = t8;
  let t9;
  if ($[24] !== currentActivityId || $[25] !== ranked) {
    t9 = ranked.find(r_0 => r_0.partner.activityId === currentActivityId) ?? ranked.find(_temp) ?? ranked[0] ?? null;
    $[24] = currentActivityId;
    $[25] = ranked;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const primary = t9;
  const selectedActivityId = pickedActivityId ?? primary?.partner.activityId ?? null;
  let t10;
  if ($[27] !== primary?.partner || $[28] !== ranked) {
    let t11;
    if ($[30] !== primary?.partner) {
      t11 = r_1 => r_1.partner.activityId !== primary?.partner.activityId;
      $[30] = primary?.partner;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    const rest = ranked.filter(t11);
    const byDistance$0 = _temp2;
    let t12;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = (a, b) => byDistance$0(a) - byDistance$0(b);
      $[32] = t12;
    } else {
      t12 = $[32];
    }
    t10 = [...rest].sort(t12);
    $[27] = primary?.partner;
    $[28] = ranked;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const alternatives = t10;
  const {
    featured: featuredAlternative,
    listed: listedAlternatives
  } = splitFeaturedAlternative({
    alternatives,
    locationQuery: location?.query,
    currentDistanceKm: primary?.distanceKm
  });
  let t11;
  if ($[33] !== partners || $[34] !== ranked) {
    let t12;
    if ($[36] !== partners) {
      t12 = r_3 => {
        const partner_1 = partners.find(p_1 => p_1.activityId === r_3.partner.activityId);
        return !!partner_1 && partnerComesToYou(partner_1);
      };
      $[36] = partners;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    t11 = byDistance(ranked.filter(t12));
    $[33] = partners;
    $[34] = ranked;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  const travelingPartners = t11;
  let t12;
  if ($[38] !== partners || $[39] !== ranked) {
    let t13;
    if ($[41] !== partners) {
      t13 = r_4 => {
        const partner_2 = partners.find(p_2 => p_2.activityId === r_4.partner.activityId);
        return !!partner_2 && partnerHasFixedVenue(partner_2);
      };
      $[41] = partners;
      $[42] = t13;
    } else {
      t13 = $[42];
    }
    t12 = byDistance(ranked.filter(t13));
    $[38] = partners;
    $[39] = ranked;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  const visitablePartners = t12;
  let t13;
  bb0: {
    if (!location) {
      let t14;
      if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = [];
        $[43] = t14;
      } else {
        t14 = $[43];
      }
      t13 = t14;
      break bb0;
    }
    let t14;
    if ($[44] !== getDrivingRoute || $[45] !== location.lat || $[46] !== location.lng || $[47] !== partners) {
      const user = {
        lat: location.lat,
        lng: location.lng
      };
      t14 = partners.map(partner_3 => {
        const closest = findClosestPartnerLocation(partner_3.addresses, user);
        const distanceKm = getDrivingRoute(partner_3.activityId)?.distanceKm ?? (closest ? haversineKm(user, closest) : null);
        return {
          partner: partner_3,
          distanceKm,
          transport: calcTransportCost({
            distanceKm,
            tiers: partner_3.transportCostTiers,
            transportMultiplier: partner_3.transportMultiplier,
            participants
          })
        };
      });
      $[44] = getDrivingRoute;
      $[45] = location.lat;
      $[46] = location.lng;
      $[47] = partners;
      $[48] = t14;
    } else {
      t14 = $[48];
    }
    t13 = t14;
  }
  const customEntries = t13;
  let t14;
  bb1: {
    if (!customEntries.length) {
      t14 = null;
      break bb1;
    }
    let t15;
    if ($[49] !== customEntries || $[50] !== primary?.partner || $[51] !== selectedActivityId) {
      t15 = customEntries.find(e_0 => e_0.partner.activityId === selectedActivityId) ?? customEntries.find(e => e.partner.activityId === primary?.partner.activityId) ?? customEntries[0] ?? null;
      $[49] = customEntries;
      $[50] = primary?.partner;
      $[51] = selectedActivityId;
      $[52] = t15;
    } else {
      t15 = $[52];
    }
    t14 = t15;
  }
  const selectedEntry = t14;
  let t15;
  bb2: {
    if (!selectedEntry) {
      t15 = null;
      break bb2;
    }
    if (!selectedEntry.transport.hasTariff) {
      t15 = null;
      break bb2;
    }
    let t16;
    if ($[53] !== customEntries || $[54] !== selectedEntry.partner.activityId || $[55] !== selectedEntry.transport.costEUR) {
      t16 = selectCheaperAlternative({
        candidates: customEntries.map(_temp3),
        selectedActivityId: selectedEntry.partner.activityId,
        selectedTransportEUR: selectedEntry.transport.costEUR,
        threshold: CHEAPER_ALTERNATIVE_THRESHOLD
      });
      $[53] = customEntries;
      $[54] = selectedEntry.partner.activityId;
      $[55] = selectedEntry.transport.costEUR;
      $[56] = t16;
    } else {
      t16 = $[56];
    }
    const chosen = t16;
    if (!chosen) {
      t15 = null;
      break bb2;
    }
    let t17;
    if ($[57] !== chosen.activityId || $[58] !== customEntries) {
      let t18;
      if ($[60] !== chosen.activityId) {
        t18 = e_1 => e_1.partner.activityId === chosen.activityId;
        $[60] = chosen.activityId;
        $[61] = t18;
      } else {
        t18 = $[61];
      }
      t17 = customEntries.find(t18);
      $[57] = chosen.activityId;
      $[58] = customEntries;
      $[59] = t17;
    } else {
      t17 = $[59];
    }
    const bestEntry = t17;
    let t18;
    if ($[62] !== chosen.activityId || $[63] !== ranked) {
      let t19;
      if ($[65] !== chosen.activityId) {
        t19 = r_5 => r_5.partner.activityId === chosen.activityId;
        $[65] = chosen.activityId;
        $[66] = t19;
      } else {
        t19 = $[66];
      }
      t18 = ranked.find(t19);
      $[62] = chosen.activityId;
      $[63] = ranked;
      $[64] = t18;
    } else {
      t18 = $[64];
    }
    const t19 = bestEntry?.transport.costEUR ?? 0;
    let t20;
    if ($[67] !== chosen.savings || $[68] !== selectedEntry.transport.costEUR || $[69] !== t18 || $[70] !== t19) {
      t20 = {
        ranked: t18,
        savings: chosen.savings,
        alternativeTransportEUR: t19,
        selectedTransportEUR: selectedEntry.transport.costEUR
      };
      $[67] = chosen.savings;
      $[68] = selectedEntry.transport.costEUR;
      $[69] = t18;
      $[70] = t19;
      $[71] = t20;
    } else {
      t20 = $[71];
    }
    t15 = t20;
  }
  const cheaperAlternative = t15;
  const selectedPartner = selectedEntry?.partner ?? null;
  let t16;
  let t17;
  if ($[72] !== location || $[73] !== mode || $[74] !== selectedPartner) {
    t16 = () => {
      if (mode !== "custom" || !location || !selectedPartner) {
        return;
      }
      const from = {
        lat: location.lat,
        lng: location.lng
      };
      const to = findClosestPartnerLocation(selectedPartner.addresses, from);
      if (!to) {
        return;
      }
      const controller = new AbortController();
      const {
        activityId
      } = selectedPartner;
      fetchDrivingRoute({
        from,
        to,
        signal: controller.signal
      }).then(route => {
        if (route) {
          setDrivingRoute({
            ...route,
            activityId,
            from
          });
        }
      }).catch(_temp4);
      return () => controller.abort();
    };
    t17 = [mode, location, selectedPartner];
    $[72] = location;
    $[73] = mode;
    $[74] = selectedPartner;
    $[75] = t16;
    $[76] = t17;
  } else {
    t16 = $[75];
    t17 = $[76];
  }
  useEffect(t16, t17);
  const t18 = "activity-pdp-location-widget";
  const t19 = "flex flex-col gap-8";
  let t20;
  if ($[77] !== setUrlMode) {
    t20 = next => {
      setWidgetMode(next);
      setUrlMode(next, {
        history: "replace"
      });
    };
    $[77] = setUrlMode;
    $[78] = t20;
  } else {
    t20 = $[78];
  }
  const t21 = !comesToYou;
  const t22 = !hasFixedLocation;
  let t23;
  if ($[79] !== mode || $[80] !== t20 || $[81] !== t21 || $[82] !== t22) {
    t23 = <ModeTabs value={mode} onChange={t20} customDisabled={t21} fixedDisabled={t22} />;
    $[79] = mode;
    $[80] = t20;
    $[81] = t21;
    $[82] = t22;
    $[83] = t23;
  } else {
    t23 = $[83];
  }
  const t24 = didAskForLocation && !location;
  let t25;
  if ($[84] !== location || $[85] !== t24) {
    t25 = <div className="pointer-events-none absolute inset-x-2 top-2 z-10"><div ref={locationInputWrap} className="pointer-events-auto max-w-sm"><LocationInputRow location={location} onLocationChange={setWidgetLocation} isAwaitingLocation={t24} /></div></div>;
    $[84] = location;
    $[85] = t24;
    $[86] = t25;
  } else {
    t25 = $[86];
  }
  let t26;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = id => setPickedActivityId(id);
    $[87] = t26;
  } else {
    t26 = $[87];
  }
  let t27;
  if ($[88] !== availableRegionNames || $[89] !== comesToYou) {
    t27 = comesToYou ? availableRegionNames : [];
    $[88] = availableRegionNames;
    $[89] = comesToYou;
    $[90] = t27;
  } else {
    t27 = $[90];
  }
  let t28;
  if ($[91] !== currentActivityId || $[92] !== eligiblePartners || $[93] !== fixedAddressId || $[94] !== hoveredActivityId || $[95] !== location || $[96] !== mode || $[97] !== selectedActivityId || $[98] !== t27) {
    t28 = <PartnerMap partners={eligiblePartners} selectedActivityId={selectedActivityId} currentActivityId={currentActivityId} selectedAddressId={fixedAddressId} onSelectAddress={_temp5} onSelect={t26} location={location} mode={mode} participants={participants} availableRegionNames={t27} highlightedActivityId={hoveredActivityId} />;
    $[91] = currentActivityId;
    $[92] = eligiblePartners;
    $[93] = fixedAddressId;
    $[94] = hoveredActivityId;
    $[95] = location;
    $[96] = mode;
    $[97] = selectedActivityId;
    $[98] = t27;
    $[99] = t28;
  } else {
    t28 = $[99];
  }
  let t29;
  if ($[100] !== t25 || $[101] !== t28) {
    t29 = <div className="relative isolate">{t25}{t28}</div>;
    $[100] = t25;
    $[101] = t28;
    $[102] = t29;
  } else {
    t29 = $[102];
  }
  let t30;
  if ($[103] !== currentActivityId || $[104] !== currentHasFixedVenue || $[105] !== currentVenueIsUnpinned || $[106] !== i18n || $[107] !== location?.query || $[108] !== mode || $[109] !== visitablePartners) {
    t30 = mode === "fixed" && !currentHasFixedVenue && <div className="flex flex-col gap-7">{currentVenueIsUnpinned ? <LocationNote>{unpinnedLocationStatement(i18n)}</LocationNote> : <ModeNotice>{visitablePartners.length > 0 ? i18n._("pdp.location.fixed.other_partners", undefined, {
          message: "This provider has no location of its own. These providers do:"
        }) : i18n._("pdp.location.fixed.no_partners", undefined, {
          message: "This provider has no location of its own."
        })}</ModeNotice>}{visitablePartners.length > 0 && <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">{visitablePartners.map(r_6 => <PartnerResultCard key={r_6.partner.activityId} ranked={r_6} userLabel={location?.query ?? null} isCurrent={r_6.partner.activityId === currentActivityId} onHoverChange={setHoveredActivityId} />)}</div>}</div>;
    $[103] = currentActivityId;
    $[104] = currentHasFixedVenue;
    $[105] = currentVenueIsUnpinned;
    $[106] = i18n;
    $[107] = location?.query;
    $[108] = mode;
    $[109] = visitablePartners;
    $[110] = t30;
  } else {
    t30 = $[110];
  }
  const t31 = mode === "fixed" && currentHasFixedVenue && <div className="flex flex-col gap-7">{primary && <PartnerResultCard ranked={primary} userLabel={userLabel} isCurrent={primary.partner.activityId === currentActivityId} onHoverChange={setHoveredActivityId} />}{featuredAlternative?.distanceKm != null && <div className="flex flex-col gap-2"><AlternativeHeader text={i18n._("pdp.location.alternative.found_partner", {
        km: Math.round(featuredAlternative.distanceKm),
        city: userLabel ?? location?.query ?? ""
      }, {
        message: "We found an alternative provider {km}km from {city}"
      })} greenFrom={String(Math.round(featuredAlternative.distanceKm))} /><PartnerResultCard ranked={featuredAlternative} userLabel={userLabel} onHoverChange={setHoveredActivityId} /></div>}{listedAlternatives.length > 0 && <div className="flex flex-col gap-2"><div className="flex items-center justify-between gap-2"><span className="text-sm font-semibold text-blue-900">{i18n._("pdp.location.alternatives.title", undefined, {
            message: "Other providers"
          })}</span></div><div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">{listedAlternatives.slice(0, 4).map(r_7 => <PartnerResultCard key={r_7.partner.activityId} ranked={r_7} userLabel={userLabel} minimal={true} onHoverChange={setHoveredActivityId} />)}</div>{listedAlternatives.length > 4 && <><div className={cx("grid transition-[grid-template-rows] duration-300 ease-out", showAllAlternatives ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}><div className="overflow-hidden"><div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">{listedAlternatives.slice(4).map(r_8 => <PartnerResultCard key={r_8.partner.activityId} ranked={r_8} userLabel={userLabel} minimal={true} onHoverChange={setHoveredActivityId} />)}</div></div></div><button type="button" onClick={() => setShowAllAlternatives(_temp6)} className="flex items-center gap-1 self-start text-sm font-semibold text-blue-900 underline">{showAllAlternatives ? i18n._("pdp.location.alternatives.show_less", undefined, {
            message: "Show less"
          }) : i18n._("pdp.location.alternatives.show_more", {
            count: listedAlternatives.length - 4
          }, {
            message: "Show {count} more"
          })}<LuChevronDown className={cx("size-4 transition-transform duration-200", showAllAlternatives && "rotate-180")} /></button></>}</div>}</div>;
  let t32;
  if ($[111] !== currentComesToYou || $[112] !== i18n || $[113] !== mode || $[114] !== travelingPartners || $[115] !== userLabel) {
    t32 = mode === "custom" && !currentComesToYou && <div className="flex flex-col gap-7"><ModeNotice>{travelingPartners.length > 0 ? i18n._("pdp.location.custom.other_partners", undefined, {
          message: "This provider does not travel to your location. These providers do:"
        }) : i18n._("pdp.location.custom.no_partners", undefined, {
          message: "This provider does not travel to your location."
        })}</ModeNotice>{travelingPartners.length > 0 && <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">{travelingPartners.map(r_9 => <PartnerResultCard key={r_9.partner.activityId} ranked={r_9} userLabel={userLabel} minimal={true} onHoverChange={setHoveredActivityId} />)}</div>}</div>;
    $[111] = currentComesToYou;
    $[112] = i18n;
    $[113] = mode;
    $[114] = travelingPartners;
    $[115] = userLabel;
    $[116] = t32;
  } else {
    t32 = $[116];
  }
  let t33;
  if ($[117] !== canQuoteTransport || $[118] !== cheaperAlternative || $[119] !== currentComesToYou || $[120] !== i18n || $[121] !== location || $[122] !== mode || $[123] !== selectedEntry || $[124] !== userLabel) {
    t33 = mode === "custom" && currentComesToYou && (location && selectedEntry ? <div className="flex flex-col gap-7"><TransportCostCard userLabel={userLabel} transport={selectedEntry.transport} />{cheaperAlternative?.ranked && <div className="flex flex-col gap-2"><AlternativeHeader text={i18n._("pdp.location.cheaper.found_saves", {
          eur: Units.formatEUR(cheaperAlternative.savings * 100)
        }, {
          message: "We found an alternative that saves {eur} on transport"
        })} greenFrom={Units.formatEUR(cheaperAlternative.savings * 100)} /><PartnerResultCard ranked={cheaperAlternative.ranked} userLabel={userLabel} transportLine={i18n._("pdp.location.cheaper.transport_line", {
          eur: Units.formatEUR(cheaperAlternative.alternativeTransportEUR * 100),
          city: userLabel ?? location.query ?? ""
        }, {
          message: "{eur} transport to {city}"
        })} onHoverChange={setHoveredActivityId} /></div>}</div> : !canQuoteTransport ? <TransportCostCard userLabel={userLabel} transport={calcTransportCost({
      distanceKm: null,
      tiers: [],
      transportMultiplier: 0
    })} /> : <button type="button" onClick={focusLocationInput} data-testid="pdp-location-calculate" className="flex w-full items-center justify-center gap-2 rounded-full bg-blue-900 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-blue-800"><LuCalculator className="size-4 shrink-0" />{i18n._("pdp.location.transport.calculate", undefined, {
        message: "Calculate your transport costs"
      })}</button>);
    $[117] = canQuoteTransport;
    $[118] = cheaperAlternative;
    $[119] = currentComesToYou;
    $[120] = i18n;
    $[121] = location;
    $[122] = mode;
    $[123] = selectedEntry;
    $[124] = userLabel;
    $[125] = t33;
  } else {
    t33 = $[125];
  }
  let t34;
  if ($[126] !== t23 || $[127] !== t29 || $[128] !== t30 || $[129] !== t31 || $[130] !== t32 || $[131] !== t33) {
    t34 = <div data-testid={t18}><div className={t19}>{t23}{t29}{t30}{t31}{t32}{t33}</div></div>;
    $[126] = t23;
    $[127] = t29;
    $[128] = t30;
    $[129] = t31;
    $[130] = t32;
    $[131] = t33;
    $[132] = t34;
  } else {
    t34 = $[132];
  }
  return t34;
};

/**
 * Which tab the widget opens on, most explicit signal first: a shared `?locationMode=` link,
 * then the `location` search filter the visitor arrived with, then the mode they last used
 * elsewhere in the flow. Availability for this activity is applied afterwards (`mode` above).
 */
export function resolveSeedMode(signals: {
  urlMode: WidgetMode | null;
  presetMode: WidgetMode | null;
  storedMode: WidgetMode | null;
}): WidgetMode {
  return signals.urlMode ?? signals.presetMode ?? signals.storedMode ?? 'fixed';
}

/**
 * Mount-only effect — the single sanctioned `useEffect` wrapper (`no-use-effect` rule):
 * components never call `useEffect` directly for lifecycle work.
 */
function useMountEffect(effect: () => void) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effect, []);
}

/**
 * A plain statement about where the activity happens. Deliberately not the orange notice: an
 * activity whose location is agreed with the provider is normal, not a problem to warn about.
 */
const LocationNote: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <LuMapPin className="mt-0.5 size-4 shrink-0 text-blue-900/40" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <p className="flex items-start gap-2 text-sm text-blue-900" data-testid="pdp-location-agreed-note">{t1}<span>{children}</span></p>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};

/**
 * Small orange notice for a mode the *viewed* partner doesn't support while a sibling
 * partner does. Never a blocker: the matching partners are listed right underneath, so
 * the customer switches provider instead of hitting a dead tab.
 */
const ModeNotice: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <LuTriangleAlert className="mt-0.5 size-4 shrink-0 text-orange-500" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <div role="status" data-testid="pdp-location-mode-notice" className="flex items-start gap-2 rounded-2xl border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">{t1}<span>{children}</span></div>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};

/**
 * Uppercase section header above a featured alternative. Everything from `greenFrom`
 * to the end of the sentence is coloured green (the distance / savings highlight).
 */
const AlternativeHeader: React.FC<{
  text: string;
  greenFrom: string;
}> = t0 => {
  const $ = _c(7);
  const {
    text,
    greenFrom
  } = t0;
  let t1;
  let t2;
  if ($[0] !== greenFrom || $[1] !== text) {
    const start = text.indexOf(greenFrom);
    t1 = "text-xs font-bold text-blue-900";
    t2 = start < 0 ? text : <>{text.slice(0, start)}<span className="text-turqoise-600">{text.slice(start)}</span></>;
    $[0] = greenFrom;
    $[1] = text;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <p className={t1}>{t2}</p>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
function _temp(r) {
  return r.badges.includes("recommended");
}
function _temp2(r_2) {
  return r_2.distanceKm ?? Infinity;
}
function _temp3(entry) {
  return {
    partner: entry.partner,
    transportEUR: entry.transport.costEUR
  };
}
function _temp4() {}
function _temp5(addressId) {
  return setWidgetFixedAddress(String(addressId));
}
function _temp6(prev) {
  return !prev;
}